import type { LegacyRegistrationRedirectConfig, LegacyRegistrationRedirectResult } from '@shared/config';

type ResolveLegacyRegistrationRedirectInput = {
  explicitRedirectUrl?: string | null;
  config?: LegacyRegistrationRedirectConfig;
};

function normaliseHostname(hostname: string): string {
  return hostname.toLowerCase();
}

function normalisePathname(pathname: string): string {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return pathname.replace(/\/$/, '');
}

function matchesQuerySubset(url: URL, requiredQuery?: Record<string, string>): boolean {
  if (!requiredQuery) {
    return true;
  }

  return Object.entries(requiredQuery).every(([key, value]) => url.searchParams.get(key) === value);
}

function parseAbsoluteUrl(urlString: string): URL | null {
  try {
    return new URL(urlString);
  } catch {
    return null;
  }
}

function resolveMappedLegacyUrl(url: URL, config: LegacyRegistrationRedirectConfig): LegacyRegistrationRedirectResult {
  const hostname = normaliseHostname(url.hostname);
  const pathname = normalisePathname(url.pathname);

  for (const mapping of config.pathMappings) {
    const mappingHosts = mapping.hosts.map(normaliseHostname);
    if (!mappingHosts.includes(hostname)) {
      continue;
    }

    const mappingPath = normalisePathname(mapping.path);
    if (mappingPath === pathname && matchesQuerySubset(url, mapping.query)) {
      return { destination: mapping.destination };
    }
  }

  return { destination: config.fallbackDestination };
}

function resolveLegacyRegistrationRedirect({ explicitRedirectUrl, config }: ResolveLegacyRegistrationRedirectInput): LegacyRegistrationRedirectResult | null {
  if (!config) {
    return null;
  }

  // The redirect_url query param, when present, is always resolved to a mapped Chroma
  // destination. Without it, we send users to the homepage with the cashier open.
  const explicit = explicitRedirectUrl?.trim();
  if (!explicit) {
    return { destination: config.fallbackDestination };
  }

  const parsedExplicit = parseAbsoluteUrl(explicit);
  if (!parsedExplicit) {
    return { destination: config.fallbackDestination };
  }

  return resolveMappedLegacyUrl(parsedExplicit, config);
}

export { resolveLegacyRegistrationRedirect };
